import React from 'react';
import { Form, Select, Typography, Switch, InputNumber, Row, Col, Tag, Space, Alert, Divider, Button, Input, List, Popconfirm } from 'antd';
import { BlockOutlined, CheckSquareOutlined, InfoCircleOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;
const { Option } = Select;

/**
 * BatchStartNodeProperties - Configuration for Batch Start node
 * Handles batch processing configuration
 */
export const BatchStartNodeProperties = ({ config, nodeId, nodes, edges, onConfigChange, onSyncBatchNodes }) => {
  // Get dynamic inputs from config
  const dynamicInputs = config.dynamicInputs || [{ id: 'input_1', name: 'input_1' }];

  // Check which inputs are connected
  const connectedInputIds = React.useMemo(() => {
    if (!edges) return new Set();
    return new Set(
      edges.filter(e => e.target === nodeId).map(e => e.targetHandle)
    );
  }, [edges, nodeId]);

  // Add new input
  const handleAddInput = () => {
    const newIndex = dynamicInputs.length + 1;
    const newInputs = [
      ...dynamicInputs,
      { id: `input_${newIndex}`, name: `input_${newIndex}` }
    ];
    onConfigChange?.({ config: { ...config, dynamicInputs: newInputs } });
    // Trigger immediate sync with batch_end
    setTimeout(() => onSyncBatchNodes?.(), 100);
  };

  // Remove input
  const handleRemoveInput = (inputId) => {
    if (dynamicInputs.length <= 1) return; // Keep at least one
    const newInputs = dynamicInputs.filter(inp => inp.id !== inputId);
    onConfigChange?.({ config: { ...config, dynamicInputs: newInputs } });
    // Trigger immediate sync with batch_end
    setTimeout(() => onSyncBatchNodes?.(), 100);
  };

  // Rename input
  const handleRenameInput = (inputId, newName) => {
    const newInputs = dynamicInputs.map(inp => 
      inp.id === inputId ? { ...inp, name: newName } : inp
    );
    onConfigChange?.({ config: { ...config, dynamicInputs: newInputs } });
    // Trigger immediate sync with batch_end
    setTimeout(() => onSyncBatchNodes?.(), 100);
  };

  return (
    <>
      <Alert
        type="info"
        icon={<BlockOutlined />}
        message="Batch Start Node"
        description="Configure input slots, then connect datasets to each slot."
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form.Item 
        label="Execution Mode" 
        name="executionMode" 
        initialValue={config.executionMode || 'sequential'}
        tooltip="How to process multiple inputs"
      >
        <Select>
          <Option value="sequential">Sequential (one at a time)</Option>
          <Option value="parallel" disabled>Parallel (coming soon)</Option>
        </Select>
      </Form.Item>

      <Form.Item 
        label="Stop on Error" 
        name="stopOnError" 
        valuePropName="checked"
        initialValue={config.stopOnError !== false}
        tooltip="Stop processing remaining iterations if one fails"
      >
        <Switch checkedChildren="Yes" unCheckedChildren="No" />
      </Form.Item>

      <Divider orientation="left" plain>
        <Text type="secondary" style={{ fontSize: 12 }}>Input Slots ({dynamicInputs.length})</Text>
      </Divider>

      <List
        size="small"
        dataSource={dynamicInputs}
        renderItem={(input, idx) => {
          const isConnected = connectedInputIds.has(input.id);
          return (
            <List.Item
              style={{ padding: '4px 0' }}
              actions={[
                <Popconfirm
                  key="delete"
                  title="Remove this input?"
                  onConfirm={() => handleRemoveInput(input.id)}
                  disabled={dynamicInputs.length <= 1 || isConnected}
                >
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<DeleteOutlined />} 
                    disabled={dynamicInputs.length <= 1 || isConnected}
                    danger
                  />
                </Popconfirm>
              ]}
            >
              <Space>
                <Tag color={isConnected ? 'green' : 'default'} style={{ minWidth: 60, textAlign: 'center' }}>
                  {idx + 1}
                </Tag>
                <Input
                  size="small"
                  value={input.name}
                  onChange={(e) => handleRenameInput(input.id, e.target.value)}
                  style={{ width: 120 }}
                  placeholder="Label"
                />
                {isConnected && <Text type="success" style={{ fontSize: 10 }}>●</Text>}
              </Space>
            </List.Item>
          );
        }}
      />

      <Button 
        type="dashed" 
        size="small" 
        icon={<PlusOutlined />} 
        onClick={handleAddInput}
        style={{ marginTop: 8, width: '100%' }}
      >
        Add Input Slot
      </Button>

      <div style={{ 
        padding: '12px', 
        backgroundColor: '#f5f5f5', 
        borderRadius: '6px', 
        fontSize: '12px', 
        marginTop: '16px' 
      }}>
        <Text type="secondary">
          <strong>Usage:</strong><br/>
          1. Add input slots for each dataset<br/>
          2. Connect datasets to each slot<br/>
          3. Nodes between Start/End run once per slot<br/>
          4. Results collected at Batch End
        </Text>
      </div>
    </>
  );
};

/**
 * BatchEndNodeProperties - Configuration for Batch End node
 * Shows the outputs that will be generated
 */
export const BatchEndNodeProperties = ({ config, nodeId, nodes, edges }) => {
  // Find paired BatchStart node by traversing back
  const pairedBatchStart = React.useMemo(() => {
    if (!nodes || !edges) return null;
    
    // BFS to find BatchStart
    const visited = new Set();
    const queue = [nodeId];
    
    while (queue.length > 0) {
      const currentId = queue.shift();
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      
      const node = nodes.find(n => n.id === currentId);
      const nodeType = node?.data?.nodeType || node?.data?.type;
      
      if (nodeType === 'batch_start') {
        return node;
      }
      
      // Add source nodes
      edges.filter(e => e.target === currentId).forEach(e => {
        queue.push(e.source);
      });
    }
    
    return null;
  }, [nodeId, nodes, edges]);

  // Get outputs from paired BatchStart
  const outputs = React.useMemo(() => {
    if (!pairedBatchStart) return [];
    
    const batchStartConfig = pairedBatchStart.data?.config || {};
    const dynamicInputs = batchStartConfig.dynamicInputs || [];
    
    if (dynamicInputs.length === 0) {
      // Count edges going into BatchStart
      const inputEdges = edges?.filter(e => e.target === pairedBatchStart.id) || [];
      return inputEdges.map((e, idx) => {
        const sourceNode = nodes?.find(n => n.id === e.source);
        const name = sourceNode?.data?.config?.datasetName || `output_${idx + 1}`;
        return { id: `output_${idx + 1}`, name };
      });
    }
    
    return dynamicInputs.map((inp, idx) => ({
      id: inp.id || `output_${idx + 1}`,
      name: inp.name || inp.id || `output_${idx + 1}`
    }));
  }, [pairedBatchStart, nodes, edges]);

  return (
    <>
      <Alert
        type="info"
        icon={<CheckSquareOutlined />}
        message="Batch End Node"
        description="Collects results from all batch iterations and provides separate outputs."
        showIcon
        style={{ marginBottom: 16 }}
      />

      {pairedBatchStart ? (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Paired with: <Tag color="purple">{pairedBatchStart.data?.label || 'Batch Start'}</Tag>
          </Text>
        </div>
      ) : (
        <Alert
          type="warning"
          message="No Batch Start Found"
          description="This Batch End node is not connected to a Batch Start node. Please ensure there's a valid path from a Batch Start node."
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Divider orientation="left" plain>
        <Text type="secondary" style={{ fontSize: 12 }}>Output Handles</Text>
      </Divider>

      {outputs.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            {outputs.map((output, idx) => (
              <Tag key={output.id} color="green">
                {idx + 1}. {output.name}
              </Tag>
            ))}
          </Space>
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            {outputs.length} output handle(s) available
          </Text>
        </div>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Outputs will be generated based on Batch Start inputs.
        </Text>
      )}

      <div style={{ 
        padding: '12px', 
        backgroundColor: '#f5f5f5', 
        borderRadius: '6px', 
        fontSize: '12px', 
        marginTop: '16px' 
      }}>
        <Text type="secondary">
          <strong>Output Behavior:</strong><br/>
          • Each iteration result becomes a separate output<br/>
          • Connect Export Excel nodes to each output for separate files<br/>
          • Output names are derived from input dataset names/tags
        </Text>
      </div>
    </>
  );
};

export default { BatchStartNodeProperties, BatchEndNodeProperties };
